import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../api/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/Card';
import { EnvelopeIcon, ArrowLeftIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import bluelogo from '../assets/bluelogo.png';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email?.trim()) {
      setError('Email adresi gerekli');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await authAPI.forgotPassword(email.trim().toLowerCase());

      if (response?.success) {
        setSuccess(true);
      } else {
        setError(response?.message || 'Bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } catch (err) {
      setError(err.message || 'Gönderim sırasında bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <CardHeader className="space-y-1 text-center pb-8">
              <div className="mx-auto w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6">
                <CheckCircleIcon className="h-12 w-12 text-green-600 dark:text-green-400" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">
                E-posta Gönderildi
              </CardTitle>
              <CardDescription className="text-gray-600 dark:text-gray-400">
                Şifre sıfırlama linki e-posta adresinize gönderildi. Lütfen gelen kutunuzu kontrol edin.
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              >
                Giriş sayfasına dön
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <CardHeader className="space-y-1 text-center pb-8">
            <div className="mx-auto w-28 h-28 bg-white dark:bg-gray-700 rounded-xl flex items-center justify-center mb-6 shadow-md border border-gray-200 dark:border-gray-600 p-3">
              <img src={bluelogo} alt="HelpDesk Logo" className="w-full h-full object-contain" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">
              Şifremi Unuttum
            </CardTitle>
            <CardDescription className="text-gray-600 dark:text-gray-400">
              E-posta adresinizi girin, size şifre sıfırlama linki gönderelim.
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-6">
              {error && (
                <div className="flex items-center p-4 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-3 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-3">
                <Label htmlFor="email" className="text-sm font-medium text-gray-900 dark:text-white">
                  E-posta
                </Label>
                <div className="relative group">
                  <EnvelopeIcon className="absolute left-3 top-3.5 h-5 w-5 text-gray-500 dark:text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="ornek@otel.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-11 h-12 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                    required
                    disabled={isLoading}
                    autoComplete="email"
                  />
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4">
              <Button
                type="submit"
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Gönderiliyor...</span>
                  </div>
                ) : (
                  'Link Gönder'
                )}
              </Button>
              <Link
                to="/login"
                className="flex items-center justify-center text-sm text-blue-600 hover:text-blue-800 dark:hover:text-blue-400"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Giriş sayfasına dön
              </Link>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
